import type { WizardStep } from '@/components/wizards';

import type { KV, RecipeFormData } from './recipePayload';
import type { RecipeWizardData, SessionSource } from './RecipeWizard.types';

// =============================================================================
// Step IDs + step definitions
// =============================================================================

export const STEP_IDS = {
  OVERVIEW: 'overview',
  INPUTS: 'inputs',
  REQUEST: 'request',
  SESSION: 'session',
  REVIEW: 'review',
} as const;

export const WIZARD_STEPS: WizardStep<RecipeWizardData>[] = [
  {
    id: STEP_IDS.OVERVIEW,
    order: 1,
    title: 'Overview',
    validate: data => data.label.trim().length > 0,
  },
  {
    id: STEP_IDS.INPUTS,
    order: 2,
    title: 'Login Credentials',
    validate: data =>
      data.inputs.some(i => i.key.trim().length > 0 && i.value.length > 0),
  },
  {
    id: STEP_IDS.REQUEST,
    order: 3,
    title: 'Login Request',
    validate: data => data.url.trim().length > 0,
  },
  {
    id: STEP_IDS.SESSION,
    order: 4,
    title: 'Capture Session',
    validate: data => SESSION_STRATEGIES[data.sessionSource].isValid(data),
  },
  {
    id: STEP_IDS.REVIEW,
    order: 5,
    title: 'Review & Save',
    isFinal: true,
    validate: () => true,
  },
];

// =============================================================================
// Content type presets
// =============================================================================

export const CONTENT_TYPE_PRESETS = [
  'application/x-www-form-urlencoded',
  'application/json',
  'multipart/form-data',
  'text/plain',
  'application/xml',
] as const;

// Sentinel used in the Content-Type Select dropdown. shadcn's <Select> forbids
// '' as an SelectItem value, so we map the "Not set" choice to this constant
// and translate at the edges.
export const CT_NONE = '__none__';

// =============================================================================
// Session strategies — the single switch
// =============================================================================
//
// Each strategy owns:
//   - `label` / `description`: copy for the SessionStep radio card.
//   - `isValid(data)`: per-strategy required-field validation.
//   - `toSteps(data)`: pure projection into the extract+capture portion of the
//     recipe wire format. wizardToRecipe() consumes this.
//
// Adding a fourth source (e.g. response_status) is one entry — not a four-place
// edit across wizardToRecipe, SessionStep, ReviewStep, and the step validator.

export interface SessionStrategy {
  label: string;
  description: string;
  isValid: (data: RecipeWizardData) => boolean;
  toSteps: (data: RecipeWizardData) => {
    extractFrom: string;
    saveAs: string;
    jsonpath: string;
    captureName: string;
    captureValue: string;
  };
}

export const SESSION_STRATEGIES: Record<SessionSource, SessionStrategy> = {
  cookie: {
    label: 'A cookie (most common)',
    description:
      'Your app responds with "Set-Cookie: session=…" and the browser sends that cookie back on every request.',
    isValid: d => d.cookieName.trim().length > 0,
    toSteps: d => {
      const saveAs = sanitizeVarName(d.cookieName) || 'session';
      return {
        extractFrom: `response_cookie:${d.cookieName}`,
        saveAs,
        jsonpath: '',
        captureName: 'Cookie',
        captureValue: `${d.cookieName}={{${saveAs}}}`,
      };
    },
  },
  response_header: {
    label: 'A response header',
    description:
      'Your app returns the session value in a response header (e.g. Authorization). We will replay that exact header on every scan.',
    isValid: d => d.headerName.trim().length > 0,
    toSteps: d => ({
      extractFrom: `response_header:${d.headerName}`,
      saveAs: 'header_value',
      jsonpath: '',
      captureName: d.headerName,
      captureValue: '{{header_value}}',
    }),
  },
  response_body: {
    label: 'A JSON token in the response body',
    description:
      'Modern API logins return JSON like {"token":"…"}. We extract that token and put it into a header on every scan.',
    isValid: d =>
      d.jsonPath.trim().length > 0 &&
      d.bodyOutputHeader.trim().length > 0 &&
      d.bodyOutputValue.trim().length > 0,
    toSteps: d => ({
      extractFrom: 'response_body',
      saveAs: 'token',
      jsonpath: d.jsonPath,
      captureName: d.bodyOutputHeader,
      captureValue: d.bodyOutputValue,
    }),
  },
};

// =============================================================================
// Initial form data
// =============================================================================

export function buildInitialData(webAppUrl: string): RecipeWizardData {
  const contentType = 'application/x-www-form-urlencoded';
  return {
    label: '',
    inputs: [
      { key: 'username', value: '' },
      { key: 'password', value: '' },
    ],
    url: defaultLoginUrl(webAppUrl),
    method: 'POST',
    contentType,
    extraHeaders: [],
    body: defaultBody(contentType),
    sessionSource: 'cookie',
    cookieName: 'session',
    headerName: 'Authorization',
    jsonPath: 'token',
    bodyOutputHeader: 'Authorization',
    bodyOutputValue: 'Bearer {{token}}',
  };
}

// defaultLoginUrl appends `/login` to the WebApp's primary URL when the input
// parses as a URL; otherwise echoes it back. The user can edit on step 3.
export function defaultLoginUrl(webAppUrl: string): string {
  if (!webAppUrl) return '';
  try {
    const u = new URL(webAppUrl);
    u.pathname = u.pathname.replace(/\/+$/, '') + '/login';
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return webAppUrl;
  }
}

// Body template per content type. Referenced by RequestStep both for the
// initial prefill and to detect "user hasn't customized the body yet" so we
// can auto-update it when Content-Type changes.
export function defaultBody(contentType: string): string {
  if (contentType === 'application/json') {
    return '{"username":"{{username}}","password":"{{password}}"}';
  }
  if (contentType === 'application/x-www-form-urlencoded') {
    return 'username={{username}}&password={{password}}';
  }
  return '';
}

// =============================================================================
// Wizard → wire format
// =============================================================================

export function wizardToRecipe(w: RecipeWizardData): RecipeFormData {
  // Content-Type comes first so it stays predictable; user extras append.
  // Empty rows in extraHeaders are dropped at serialize time by toRecipePayload.
  const headers: KV[] = [
    ...(w.contentType ? [{ key: 'Content-Type', value: w.contentType }] : []),
    ...w.extraHeaders,
  ];

  const session = SESSION_STRATEGIES[w.sessionSource].toSteps(w);

  return {
    label: w.label,
    inputs: w.inputs,
    steps: [
      {
        kind: 'http',
        method: w.method,
        url: w.url,
        headers,
        body: w.body,
      },
      {
        kind: 'extract',
        from: session.extractFrom,
        jsonpath: session.jsonpath,
        regex: '',
        save_as: session.saveAs,
      },
      {
        kind: 'capture',
        name: session.captureName,
        value: session.captureValue,
      },
    ],
  };
}

function sanitizeVarName(name: string): string {
  return name.replace(/[^A-Za-z0-9_]/g, '_');
}
